import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, X, ShieldCheck, Play, CheckCircle2, ChevronRight,
} from "lucide-react";
import { Pill } from "./Pill";
import {
  FRAMEWORK_META, RISK_TONE, STATUS_TONE, READINESS_TONE, READINESS_LABEL,
} from "./constants";

export function FrameworkDetail({ frameworkId, onClose, onRun, running }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("gaps");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/regulatory/frameworks/${frameworkId}`);
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e, "Failed to load framework"));
        onClose();
      } finally { setLoading(false); }
    })();
  }, [frameworkId, onClose]);

  if (loading || !data) {
    return (
      <div className="hw-reg__drawer-backdrop" onClick={onClose}>
        <div className="hw-reg__drawer" onClick={(e) => e.stopPropagation()}>
          <div className="hw-uat-cycles__loading" style={{ padding: "3rem" }}>
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        </div>
      </div>
    );
  }
  const s = data.score || {};
  const meta = FRAMEWORK_META[frameworkId] || {};
  return (
    <div className="hw-reg__drawer-backdrop" onClick={onClose}>
      <div className="hw-reg__drawer" onClick={(e) => e.stopPropagation()}
           data-testid={`framework-detail-${frameworkId}`}>
        <div className="hw-reg__drawer-head">
          <div>
            <div className="hw-reg__drawer-crumb">Framework</div>
            <div className="hw-reg__drawer-title" style={{ color: meta.color }}>
              <ShieldCheck className="w-4 h-4" /> {data.framework.short_name}
              <span style={{ color: "#64748B", fontSize: "0.85rem", fontWeight: 400 }}>
                {data.framework.version}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button type="button" className="hw-reg__btn hw-reg__btn--primary"
                    onClick={() => onRun(frameworkId)}
                    disabled={running === frameworkId}
                    data-testid={`fw-detail-run-${frameworkId}`}>
              {running === frameworkId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Run assessment
            </button>
            <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose}
                    data-testid="framework-detail-close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="hw-reg__drawer-body">
          <section className="hw-reg__section">
            <h4>{data.framework.name}</h4>
            <p style={{ fontSize: "0.88rem", color: "#334155" }}>{data.framework.description}</p>
            <div className="hw-reg__stat-row">
              <div className="hw-reg__stat">
                <div className="hw-reg__stat-label">Weighted score</div>
                <div className="hw-reg__stat-value" style={{ color: meta.color }}>{s.weighted_score}%</div>
              </div>
              <div className="hw-reg__stat">
                <div className="hw-reg__stat-label">Readiness</div>
                <div className="hw-reg__stat-value">
                  <Pill tone={READINESS_TONE[s.readiness]}>{READINESS_LABEL[s.readiness]}</Pill>
                </div>
              </div>
              <div className="hw-reg__stat">
                <div className="hw-reg__stat-label">Applicable controls</div>
                <div className="hw-reg__stat-value">{s.applicable_controls || 0}</div>
              </div>
              <div className="hw-reg__stat">
                <div className="hw-reg__stat-label">Gaps</div>
                <div className="hw-reg__stat-value" style={{ color: "#B45309" }}>{s.gap_count || 0}</div>
              </div>
            </div>
          </section>

          <section className="hw-reg__section">
            <div className="hw-reg__subtabs">
              <button type="button"
                      className={`hw-reg__subtab${tab === "gaps" ? " is-active" : ""}`}
                      onClick={() => setTab("gaps")}
                      data-testid="fw-detail-tab-gaps">
                Gap Analysis ({s.gap_count || 0})
              </button>
              <button type="button"
                      className={`hw-reg__subtab${tab === "breakdown" ? " is-active" : ""}`}
                      onClick={() => setTab("breakdown")}
                      data-testid="fw-detail-tab-breakdown">
                Breakdown
              </button>
            </div>
            {tab === "gaps" && (
              <ul className="hw-reg__gaps" data-testid="fw-gaps-list">
                {(s.gap_analysis || []).map((g) => (
                  <li key={g.control_id}>
                    <div className="hw-reg__gap-head">
                      <code>{g.control_id}</code>
                      <span>{g.title}</span>
                      <Pill tone={RISK_TONE[g.risk_level]}>{RISK_TONE[g.risk_level]?.label}</Pill>
                      <Pill tone={STATUS_TONE[g.current_status]}>{STATUS_TONE[g.current_status]?.label}</Pill>
                    </div>
                    <div className="hw-reg__gap-req">
                      {g.framework_requirement ? <><strong>{g.framework_requirement}</strong> · </> : null}
                      {g.section}
                    </div>
                    <div className="hw-reg__gap-desc">{g.gap_description}</div>
                    {g.remediation ? <div className="hw-reg__gap-rem"><em>Remediation:</em> {g.remediation}</div> : null}
                  </li>
                ))}
                {(!s.gap_analysis || s.gap_analysis.length === 0) && (
                  <li style={{ color: "#065F46", background: "#ECFDF5" }}>
                    <CheckCircle2 className="w-4 h-4" style={{ display: "inline", marginRight: "0.4rem" }} />
                    No gaps — every applicable control is compliant.
                  </li>
                )}
              </ul>
            )}
            {tab === "breakdown" && (
              <div className="hw-reg__breakdown">
                <div>
                  <h5>By domain</h5>
                  {Object.entries(s.per_domain || {}).map(([k, v]) => (
                    <div key={k} className="hw-reg__bar-row">
                      <span className="hw-reg__bar-label">{k}</span>
                      <div className="hw-reg__bar-wrap"><div className="hw-reg__bar" style={{ width: `${v || 0}%` }} /></div>
                      <span className="hw-reg__bar-value">{v ?? "—"}%</span>
                    </div>
                  ))}
                </div>
                <div>
                  <h5>By risk level</h5>
                  {["critical", "high", "medium", "low"].map((r) => {
                    const v = (s.per_risk || {})[r];
                    if (v == null) return null;
                    return (
                      <div key={r} className="hw-reg__bar-row">
                        <span className="hw-reg__bar-label">{r}</span>
                        <div className="hw-reg__bar-wrap"><div className="hw-reg__bar" style={{ width: `${v}%`, background: RISK_TONE[r]?.color }} /></div>
                        <span className="hw-reg__bar-value">{v}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// Named export just so the icon import above isn't unused if refactored.
export const _ChevronRight = ChevronRight;  // eslint-disable-line no-unused-vars
